import React from 'react';
import { cn } from '../../utils/cn';
import { Loader2 } from 'lucide-react';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';
export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'link' | 'success' | 'danger';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  glass?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading, glass, fullWidth, leftIcon, children, ...props }, ref) => {
    const baseStyles = 'inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';
    const glassStyles = glass ? 'backdrop-blur-md bg-opacity-20' : '';
    const fullWidthStyles = fullWidth ? 'w-full' : '';

    const variants = {
      primary: 'bg-gray-900 text-white hover:bg-gray-800 rounded-lg',
      secondary: 'bg-gray-600 text-white hover:bg-gray-700 rounded-lg',
      outline: 'border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-lg',
      ghost: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 rounded-lg',
      link: 'text-gray-900 underline-offset-4 hover:underline',
      success: 'bg-green-600 text-white hover:bg-green-700 rounded-lg',
      danger: 'bg-red-600 text-white hover:bg-red-700 rounded-lg'
    };

    const sizes = {
      sm: 'h-9 px-3 text-sm',
      md: 'h-10 px-4',
      lg: 'h-11 px-8',
      icon: 'h-10 w-10'
    };

    const buttonStyles = cn(
      baseStyles,
      variants[variant],
      sizes[size],
      glassStyles,
      fullWidthStyles,
      className
    );

    return (
      <button
        ref={ref}
        className={buttonStyles}
        disabled={loading || props.disabled}
        {...props}
      >
        {loading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : leftIcon ? (
          <span className="mr-2">{leftIcon}</span>
        ) : null}
        {children}
      </button>
    );
  }
);